import createClient, { type Middleware } from 'openapi-fetch'
import { useAuthStore } from '@/store/authStore'
import type { paths } from './generated'

const baseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'

const bearerMiddleware: Middleware = {
  onRequest({ request }) {
    const token = useAuthStore.getState().token
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`)
    }
    return request
  },
}

const unauthorizedMiddleware: Middleware = {
  onResponse({ response, request }) {
    if (response.status !== 401) return response
    // Avoid recursive logout call when /api/auth/me itself returns 401 during hydrate —
    // the store handles that path internally.
    const isMeProbe = request.url.endsWith('/api/auth/me')
    if (!isMeProbe) {
      useAuthStore.getState().logout({ skipServerCall: true })
    }
    return response
  },
}

// openapi-fetch captures `globalThis.fetch` at construction time. In Vitest +
// MSW, the mock server patches `globalThis.fetch` later (in beforeAll), after
// this module has already evaluated — so a frozen reference would bypass MSW.
// Forwarding through a wrapper re-reads the current global fetch on each call.
const dynamicFetch: typeof fetch = (...args) => globalThis.fetch(...args)

export const apiClient = createClient<paths>({ baseUrl, fetch: dynamicFetch })
apiClient.use(bearerMiddleware, unauthorizedMiddleware)
